import { isArray, isNotEmpty, toJSON, unique } from '@rehaje/shared'

import { UnknownSchema } from '../../constants'
import { SchemaDefinition } from '../../models/SchemaDefinition'

import type { SchemaMapSpecific } from '../../types'

export class SchemaMapDefinition extends SchemaDefinition {
  override defaultsMerge(value: any, schema: SchemaMapSpecific) {
    return value ?? this.getDefaults(schema)
  }

  getDefaults(schema: SchemaMapSpecific): any | undefined {
    return new Function(
      `return new Map(${JSON.stringify(toJSON(schema.entries) ?? [])})`
    )()
  }

  override getIn(schema: SchemaMapSpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    return UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaMapSpecific,
    path: PropertyKey[]
  ): SchemaMapSpecific {
    if (!(target instanceof Map) || !this.test(target, schema, path)) {
      return { ...schema, type: 'Schema.Map' }
    }
    return {
      ...schema,
      entries: Array.from(target?.entries() ?? []),
      type: 'Schema.Map'
    }
  }

  override inferOnlySchema(schema: SchemaMapSpecific) {
    return schema
  }

  override inferTypeScript(
    schema: SchemaMapSpecific,
    path: PropertyKey[]
  ): string {
    const { entries } = schema ?? {}
    if (isArray(entries)) {
      const keyTypes = unique(
        entries.map((entry, index) =>
          super.inferTypeScript(
            this.inferShapeSchema(entry[0]),
            path.concat(index)
          )
        )
      )
      const valueTypes = unique(
        entries.map((entry, index) =>
          super.inferTypeScript(
            this.inferShapeSchema(entry[1]),
            path.concat(index)
          )
        )
      )
      return `Map<${keyTypes.length > 0 ? keyTypes.join(' | ') : 'any'}, ${valueTypes.length > 0 ? valueTypes.join(' | ') : 'any'}>`
    }
    return `Map<any,any>`
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaMapSpecific,
    path: PropertyKey[]
  ) {
    if (target === null && schema.nullable) return target
    if (!this.test(target, schema, path)) return this.getDefaults(schema)
    return target
  }

  override validate(target: any, schema: SchemaMapSpecific): void {
    this.validateMetadata(
      target,
      schema,
      (value) => value instanceof Map,
      isNotEmpty
    )
  }
}
